<ng-template #mfaBody>
  <div
    class="two-factor__tabs"
    role="radiogroup"
    [attr.aria-label]="'AUTH.MFA.CHOOSE_METHOD' | translate">
    <button
      type="button"
      class="two-factor__tab"
      role="radio"
      [class.is-active]="mfaType === 'totp'"
      [attr.aria-checked]="mfaType === 'totp'"
      (click)="switchToTotpMfa()">
      <i class="ki-outline ki-phone" aria-hidden="true"></i>
      <span>{{ 'AUTH.MFA.AUTH_APP' | translate }}</span>
    </button>
    <button
      type="button"
      class="two-factor__tab"
      role="radio"
      [class.is-active]="mfaType === 'email'"
      [attr.aria-checked]="mfaType === 'email'"
      (click)="switchToEmailMfa()">
      <i class="ki-outline ki-sms" aria-hidden="true"></i>
      <span>{{ 'AUTH.MFA.EMAIL_VERIFICATION' | translate }}</span>
    </button>
  </div>

  <div class="two-factor__body" [class.shake]="shake">
    <div class="text-center mb-6" *ngIf="mfaType === 'totp' && qrCodeUrl && !isModalMode">
      <img [src]="qrCodeUrl" [alt]="'AUTH.MFA.SCAN_QR' | translate" class="two-factor__qr" />
      <p class="two-factor__hint mb-0">{{ 'AUTH.MFA.SCAN_QR' | translate }}</p>
      <div class="mt-4" *ngIf="secretKey">
        <app-portal-button
          layout="label"
          variant="link"
          size="sm"
          (pressed)="showSecretKey = !showSecretKey">
          {{ (showSecretKey ? ('AUTH.MFA.HIDE' | translate) : ('AUTH.MFA.SHOW' | translate)) + ' ' + ('AUTH.MFA.SECRET_KEY' | translate) }}
        </app-portal-button>
        <div *ngIf="showSecretKey" class="mt-2">
          <p class="two-factor__hint">
            <strong>{{ 'AUTH.MFA.MANUAL_ENTRY' | translate }}</strong>
            {{ 'AUTH.MFA.MANUAL_ENTRY_DESC' | translate }}
          </p>
          <div class="two-factor__secret">
            <code>{{ secretKey }}</code>
            <app-portal-button
              layout="label"
              variant="outline-primary"
              size="sm"
              (pressed)="copyToClipboard(secretKey)">
              {{ 'AUTH.MFA.COPY' | translate }}
            </app-portal-button>
          </div>
        </div>
      </div>
    </div>

    <p class="two-factor__hint text-center" *ngIf="mfaType === 'email'">
      {{ 'AUTH.MFA.EMAIL_INFO' | translate }}
    </p>

    <form class="two-factor__form" [formGroup]="twoFactorForm" (ngSubmit)="$event.preventDefault()">
      <div class="two-factor__error-well" *ngIf="hasError" role="alert">
        <strong>{{ 'AUTH.SIGN_IN.ERROR_TITLE' | translate }}</strong>
        <span>{{ errorMessage }}</span>
      </div>

      <ng-container *ngIf="!showBackupCodeField">
        <label class="visually-hidden" [attr.for]="'two-factor-otp-' + otpPrefix + '-1'">
          {{ (mfaType === 'email' ? 'AUTH.MFA.TYPE_6_DIGIT_EMAIL' : 'AUTH.MFA.TYPE_6_DIGIT') | translate }}
        </label>
        <p class="two-factor__hint text-center" *ngIf="mfaType !== 'email'">
          {{ 'AUTH.MFA.ENTER_CODE_APP' | translate }}
        </p>
        <div class="two-factor__otp">
          <input
            *ngFor="let slot of codeSlots"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="1"
            class="form-control two-factor__otp-cell"
            [id]="'two-factor-otp-' + otpPrefix + '-' + slot"
            [attr.name]="otpPrefix + '_' + slot"
            [formControlName]="otpPrefix + '_' + slot"
            (input)="onCodeInput($event, slot, otpPrefix)"
            (keydown)="onCodeKeydown($event, slot, otpPrefix)"
            (paste)="onCodePaste($event, otpPrefix)"
            [class.is-invalid]="twoFactorForm.get(otpPrefix + '_' + slot)?.invalid && twoFactorForm.get(otpPrefix + '_' + slot)?.touched"
            [style.borderColor]="getInputBorderColor(slot, otpPrefix)" />
        </div>
        <p *ngIf="showIncompleteCodeError" class="two-factor__error" role="alert">
          {{ (mfaType === 'email' ? 'AUTH.MFA.COMPLETE_EMAIL_CODE_REQUIRED' : 'AUTH.MFA.COMPLETE_CODE_REQUIRED') | translate }}
        </p>
      </ng-container>

      <div class="mb-4" *ngIf="showBackupCodeField">
        <label class="two-factor__label" for="two-factor-backup">{{ 'AUTH.MFA.ENTER_BACKUP_CODE' | translate }}</label>
        <input
          id="two-factor-backup"
          class="form-control text-center"
          type="text"
          formControlName="backupCode"
          autocomplete="off"
          [attr.placeholder]="'AUTH.MFA.ENTER_BACKUP_CODE' | translate"
          [class.is-invalid]="twoFactorForm.get('backupCode')?.invalid && twoFactorForm.get('backupCode')?.touched" />
        <p
          class="two-factor__error"
          role="alert"
          *ngIf="twoFactorForm.get('backupCode')?.invalid && twoFactorForm.get('backupCode')?.touched">
          <ng-container *ngIf="twoFactorForm.get('backupCode')?.errors?.['required']">
            {{ 'AUTH.MFA.BACKUP_REQUIRED' | translate }}
          </ng-container>
          <ng-container *ngIf="twoFactorForm.get('backupCode')?.errors?.['minlength'] || twoFactorForm.get('backupCode')?.errors?.['maxlength']">
            {{ 'AUTH.MFA.BACKUP_INVALID' | translate }}
          </ng-container>
        </p>
      </div>

      <div class="two-factor__actions">
        <app-portal-button
          layout="label"
          variant="primary"
          type="submit"
          extraClass="two-factor__submit"
          [disabled]="submitDisabled"
          [loading]="(isLoading$ | async) === true"
          (pressed)="showBackupCodeField ? submitBackupCode() : submit()">
          {{ submitLabelKey | translate }}
        </app-portal-button>
      </div>

      <div class="two-factor__alt">
        <app-portal-button
          *ngIf="mfaType === 'email' && !showBackupCodeField"
          layout="label"
          variant="link"
          (pressed)="resendCode()">
          {{ 'AUTH.MFA.RESEND_EMAIL_CODE' | translate }}
        </app-portal-button>
        <app-portal-button
          *ngIf="!showBackupCodeField"
          layout="label"
          variant="link"
          (pressed)="showBackupCodeInput()">
          {{ 'AUTH.MFA.USE_BACKUP' | translate }}
        </app-portal-button>
        <app-portal-button
          *ngIf="showBackupCodeField"
          layout="label"
          variant="link"
          (pressed)="showBackupCodeField = false; hasError = false; errorMessage = ''">
          {{ 'AUTH.MFA.BACK_TO_CODE' | translate }}
        </app-portal-button>
      </div>
    </form>
  </div>

  <div class="two-factor__backups" *ngIf="backupCodes.length > 0">
    <p class="two-factor__hint">
      <strong>{{ 'AUTH.MFA.NEED_HELP' | translate }}</strong>
      {{ 'AUTH.MFA.USE_BACKUP_HINT' | translate }}
    </p>
    <div class="two-factor__backup-grid">
      <app-portal-button
        *ngFor="let code of backupCodes; let i = index"
        layout="label"
        variant="outline-primary"
        extraClass="w-100"
        (pressed)="useBackupCode(code)">
        {{ 'AUTH.MFA.USE_BACKUP' | translate }} {{ i + 1 }}
      </app-portal-button>
    </div>
  </div>
</ng-template>

<app-portal-modal-shell
  *ngIf="isModalMode"
  [title]="'AUTH.MFA.TITLE' | translate"
  [subtitle]="'AUTH.MFA.CHOOSE_METHOD' | translate"
  size="md"
  [showOverlay]="!activeModal"
  (dismiss)="closeModal()">
  <div portalModalLead class="two-factor__header-icon two-factor__header-icon--on-dark" aria-hidden="true">
    <i class="ki-outline ki-shield-tick"></i>
    <i class="ki-outline ki-lock"></i>
  </div>
  <div class="two-factor two-factor--modal">
    <ng-container *ngTemplateOutlet="mfaBody"></ng-container>
  </div>
</app-portal-modal-shell>

<div class="two-factor two-factor--page" *ngIf="!isModalMode">
  <header class="two-factor__header">
    <div class="two-factor__heading">
      <div class="two-factor__header-icon" aria-hidden="true">
        <i class="ki-outline ki-shield-tick"></i>
        <i class="ki-outline ki-lock"></i>
      </div>
      <div>
        <h1 id="twofactor-title" class="two-factor__title">{{ 'AUTH.MFA.TITLE' | translate }}</h1>
        <p class="two-factor__subtitle">{{ 'AUTH.MFA.CHOOSE_METHOD' | translate }}</p>
      </div>
    </div>
  </header>
  <ng-container *ngTemplateOutlet="mfaBody"></ng-container>
</div>
